import React, {ChangeEvent} from 'react';
import {Button, CircularProgress, TextField} from '@material-ui/core';
import DiaryModel, {DiaryEntry} from 'one.models/lib/models/DiaryModel';
import {useHistory} from 'react-router-dom';
import i18n from '../../i18n';
import '../../Primary.css';
import Paper from '@material-ui/core/Paper';
import './DiaryEntry.css';
import {ObjectData} from 'one.models/lib/models/ChannelManager';
import InfoMessage, {MessageType} from '../errors/InfoMessage';
import MenuButton from '../menu/MenuButton';
import {displayCircularProgress, formatDate, hideCircularProgress} from '../utils/Utils';

/**
 * DiaryEntryForm
 * @param {{}} props
 * @param {DiaryModel} props.diaryModel
 * @returns {React.ReactElement}
 */
export default function DiaryEntryPage(props: {diaryModel: DiaryModel}): React.ReactElement {
    const history = useHistory();

    const [diaryEntryDescription, setDiaryEntryDescription] = React.useState('');
    const [diaryEntryDate, setDiaryEntryDate] = React.useState<Date>();
    const [errorState, setErrorState] = React.useState('');
    const urlSplit = history.location.pathname.split('/');
    const isViewMode = urlSplit.includes('view');
    const diaryId = urlSplit[urlSplit.length - 1];
    const hasDiaryId = /\d/.test(diaryId);
    const [displayError, setDisplayError] = React.useState(false);

    async function getDiaryEntryObject(): Promise<ObjectData<DiaryEntry> | undefined> {
        if (hasDiaryId) {
            return await props.diaryModel.getEntryById(diaryId);
        } else {
            return;
        }
    }

    React.useEffect(() => {
        function fetchDescription(): void {
            if (!diaryEntryDate) {
                getDiaryEntryObject()
                    .then(entry => {
                        if (entry) {
                            setDiaryEntryDescription(entry.data);
                            setDiaryEntryDate(entry.creationTime);
                        }
                        hideCircularProgress();
                    })
                    .catch(err => {
                        hideCircularProgress();
                        console.error('Error: ', err);
                    });
                return;
            }

            if (!hasDiaryId) {
                setDiaryEntryDescription('');
            }
            hideCircularProgress();
        }
        fetchDescription();
    }, [isViewMode]);

    async function sendDiaryEntry(): Promise<void> {
        displayCircularProgress();
        await props.diaryModel
            .addEntry(diaryEntryDescription)
            .then(() => {
                hideCircularProgress();
            })
            .catch(err => {
                hideCircularProgress();
                setErrorState(err.message);
                setDisplayError(true);
            });

        if (diaryEntryDescription) {
            history.push('/journal');
        }
    }

    function createDiaryEntryCopy(): void {
        history.push('/diary/' + diaryId);
    }

    return (
        <>
            <div className="circular-progress-container">
                <CircularProgress className="circular-progress" size={35} />
            </div>
            <div
                className={`page-container hide ${
                    isViewMode ? 'diary-entry-paper-view-mode' : 'diary-entry-paper'
                }`}
            >
                {errorState ? (
                    <InfoMessage
                        errorMessage={errorState}
                        displayMessage={displayError}
                        setDisplayMessage={setDisplayError}
                        messageType={MessageType.Error}
                    />
                ) : (
                    <></>
                )}
                <div className="menu-button-header">
                    <MenuButton />
                    <h2 className="headline"> {i18n.t('common:eventTypes.diaryEntry')}</h2>
                </div>
                {isViewMode ? (
                    <div className="title-element">
                        {diaryEntryDate ? formatDate(diaryEntryDate) : ''}
                    </div>
                ) : (
                    <></>
                )}
                <Paper square elevation={3} className="page-content-box">
                    {isViewMode ? (
                        <div className="diary-entry-view-mode">
                            <div className="diary-entry-description">{diaryEntryDescription}</div>
                        </div>
                    ) : (
                        <>
                            <div className="text-box">
                                <TextField
                                    fullWidth={true}
                                    value={diaryEntryDescription}
                                    id="outlined-basic"
                                    variant="outlined"
                                    multiline
                                    rows={10}
                                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                        setDiaryEntryDescription(e.target.value);
                                    }}
                                />
                            </div>
                        </>
                    )}
                </Paper>
                <div className="buttons-container">
                    {isViewMode ? (
                        <Button
                            className="button"
                            variant="contained"
                            color="primary"
                            onClick={createDiaryEntryCopy}
                        >
                            {i18n.t('common:buttons.copy')}
                        </Button>
                    ) : (
                        <Button
                            onClick={sendDiaryEntry}
                            className="button"
                            variant="contained"
                            color="primary"
                        >
                            {i18n.t('common:buttons.submit')}
                        </Button>
                    )}
                </div>
            </div>
        </>
    );
}
